import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioOption } from '@/components/ui/radio-group';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { parseAmountInput } from '../money';
import { PHONE_BOTTOM_SHEET } from '../reports/phone-sheet';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import {
  getFlatRateSetting,
  previewFlatRateSetting,
  putFlatRateSetting,
  sweepOfProblem,
  type FlatRateSetting,
  type FlatRateSweep,
} from './api';

type Mode = FlatRateSetting['mode'];
const MODES: readonly Mode[] = ['on', 'off'];
/** The server's answer to anyone without `expenses:expense:manage`, e.g. a role removed meanwhile. */
const FORBIDDEN = 'REPORT_FORBIDDEN';

/** A daily rate as typed: euro above zero, at most two decimals; null otherwise. */
function dailyOf(input: string): string | null {
  const amount = parseAmountInput(input);
  return amount !== null && /[1-9]/.test(amount) ? amount : null;
}

/** «46.48» → «46,48» for the input. */
const shown = (amount: string | null): string => (amount ? amount.replace('.', ',') : '');

/**
 * Gestione's «Forfettario trasferte» (wave 2, R-b): on or off, and the daily rate for a trip in
 * Italy and abroad. Audited by the server; a change of rates applies to the lines added
 * afterwards, the lines already on a note keep their rate. Switching it off or on sweeps every
 * note not yet approved (decision 197): the page asks the server how many first, and saves only
 * once the person said yes.
 */
export function FlatRateSettingsPage() {
  const permissions = useExpensesPermissions();
  if (permissions.status === 'loading') return null;
  if (!holds(permissions, EXPENSES_MANAGE)) return <Navigate to="/expenses" replace />;
  return <SettingsForm />;
}

function SettingsForm() {
  const { t } = useTranslation('expenses');
  const [loaded, setLoaded] = useState<'loading' | 'failed' | 'forbidden' | 'ready'>('loading');
  const [mode, setMode] = useState<Mode>('off');
  const [italy, setItaly] = useState('');
  const [abroad, setAbroad] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<'idle' | 'saved' | 'failed' | 'ratesRequired' | 'forbidden'>(
    'idle',
  );
  /** The mode as stored: a save that changes it sweeps the notes, so it is previewed first. */
  const [storedMode, setStoredMode] = useState<Mode>('off');
  const [confirming, setConfirming] = useState<{
    readonly sweep: FlatRateSweep;
    /** The numbers changed since the preview the person saw. */
    readonly changed: boolean;
  } | null>(null);
  const [swept, setSwept] = useState<FlatRateSweep | null>(null);

  const fill = (setting: FlatRateSetting): void => {
    setMode(setting.mode);
    setStoredMode(setting.mode);
    setItaly(shown(setting.italyDaily));
    setAbroad(shown(setting.abroadDaily));
  };

  useEffect(() => {
    let live = true;
    void getFlatRateSetting().then((outcome) => {
      if (!live) return;
      if (!outcome.ok) return setLoaded(outcome.code === FORBIDDEN ? 'forbidden' : 'failed');
      fill(outcome.value);
      setLoaded('ready');
    });
    return () => {
      live = false;
    };
  }, []);

  const italyDaily = dailyOf(italy);
  const abroadDaily = dailyOf(abroad);
  const valid = mode === 'off' || (italyDaily !== null && abroadDaily !== null);

  const body: FlatRateSetting =
    mode === 'on'
      ? { mode, italyDaily, abroadDaily }
      : { mode, italyDaily: null, abroadDaily: null };

  const refused = (code: string): void => {
    if (code === 'RATES_REQUIRED') setSaved('ratesRequired');
    else setSaved(code === FORBIDDEN ? 'forbidden' : 'failed');
  };

  /** None to change: it saves at once, confirming zero; else it asks the person. */
  const ask = async (sweep: FlatRateSweep, changed: boolean): Promise<void> => {
    if (sweep.notes > 0) {
      setBusy(false);
      setConfirming({ sweep, changed });
      return;
    }
    await write(sweep);
  };

  /** Asks the server how many notes and lines the switch changes. */
  const preview = async (): Promise<void> => {
    const outcome = await previewFlatRateSetting(body);
    if (!outcome.ok) {
      setBusy(false);
      return refused(outcome.code);
    }
    await ask(outcome.value, false);
  };

  const write = async (confirm?: FlatRateSweep): Promise<void> => {
    setBusy(true);
    const outcome = await putFlatRateSetting(body, confirm);
    if (!outcome.ok && outcome.code === 'SWEEP_UNCONFIRMED') {
      // The mode changed elsewhere since this page read it: show what the switch does now.
      return preview();
    }
    const found =
      !outcome.ok && outcome.code === 'SWEEP_CHANGED' ? sweepOfProblem(outcome.problem) : null;
    if (found !== null) {
      // The notes changed since the preview: the person confirms the numbers found now.
      return ask(found, true);
    }
    setBusy(false);
    setConfirming(null);
    if (!outcome.ok) return refused(outcome.code);
    fill(outcome.value);
    setSwept(outcome.value.swept.notes > 0 ? outcome.value.swept : null);
    setSaved('saved');
  };

  const save = async (): Promise<void> => {
    if (!valid) return;
    setSwept(null);
    setBusy(true);
    if (mode !== storedMode) return preview();
    await write();
  };

  return (
    <section aria-label={t('flatRate.settings.title')} className="flex max-w-xl flex-col gap-4">
      <PageHead title={t('flatRate.settings.title')} summary={t('flatRate.settings.summary')} />
      {loaded === 'loading' ? <p role="status">{t('flatRate.loading')}</p> : null}
      {loaded === 'failed' ? <p role="alert">{t('flatRate.loadFailed')}</p> : null}
      {loaded === 'forbidden' ? <p role="alert">{t('flatRate.settings.forbidden')}</p> : null}
      {loaded === 'ready' ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setSaved('idle');
            void save();
          }}
        >
          <RadioGroup legend={t('flatRate.settings.mode')}>
            {MODES.map((value) => (
              <RadioOption
                key={value}
                name="mode"
                label={t(`flatRate.settings.modes.${value}`)}
                help={t(`flatRate.settings.modeHelp.${value}`)}
                checked={mode === value}
                onSelect={() => setMode(value)}
              />
            ))}
          </RadioGroup>
          {mode === 'on' ? (
            <div className="grid gap-4 @min-[480px]/shell:grid-cols-2">
              <RateField
                label={t('flatRate.settings.italyDaily')}
                value={italy}
                ok={italyDaily !== null}
                onChange={setItaly}
              />
              <RateField
                label={t('flatRate.settings.abroadDaily')}
                value={abroad}
                ok={abroadDaily !== null}
                onChange={setAbroad}
              />
            </div>
          ) : null}
          {saved === 'saved' ? (
            <p role="status">
              {swept === null
                ? t('flatRate.settings.saved')
                : t(`flatRate.settings.swept.${swept.sweep === 'add' ? 'add' : 'remove'}`, {
                    notes: swept.notes,
                    lines: swept.lines,
                  })}
            </p>
          ) : null}
          {saved === 'ratesRequired' || saved === 'failed' || saved === 'forbidden' ? (
            <p role="alert" className="text-destructive text-sm">
              {t(`flatRate.settings.${saved}`)}
            </p>
          ) : null}
          <div className="flex flex-col items-start gap-1">
            <Button
              type="submit"
              disabled={!valid || busy}
              aria-describedby={valid ? undefined : 'flat-rate-save-reason'}
            >
              {t('flatRate.settings.save')}
            </Button>
            {valid ? null : (
              <small id="flat-rate-save-reason" className="text-muted-foreground text-sm">
                {t('flatRate.settings.saveReason')}
              </small>
            )}
          </div>
        </form>
      ) : null}
      {confirming !== null ? (
        <SweepSheet
          sweep={confirming.sweep}
          changed={confirming.changed}
          busy={busy}
          onConfirm={() => void write(confirming.sweep)}
          onClose={() => setConfirming(null)}
        />
      ) : null}
    </section>
  );
}

/** Decision 197: what switching the flat rate does to the notes, in words, before it is done. */
function SweepSheet({
  sweep,
  changed,
  busy,
  onConfirm,
  onClose,
}: {
  readonly sweep: FlatRateSweep;
  readonly changed: boolean;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('expenses');
  const direction = sweep.sweep === 'add' ? 'add' : 'remove';
  const notes = t('flatRate.sweep.notes', { count: sweep.notes });
  const lines = t('flatRate.sweep.lines', { count: sweep.lines });
  return (
    <Sheet open onOpenChange={(next) => (next ? undefined : onClose())}>
      <SheetContent side="bottom" className={`${PHONE_BOTTOM_SHEET} flex flex-col gap-4 p-4`}>
        <SheetTitle>{t(`flatRate.sweep.title.${direction}`)}</SheetTitle>
        <SheetDescription className="text-foreground text-base">
          {t(`flatRate.sweep.what.${direction}`, { notes, lines })}
        </SheetDescription>
        {changed ? (
          <p role="status" className="text-sm font-semibold">
            {t('flatRate.sweep.changed')}
          </p>
        ) : null}
        <p className="text-muted-foreground text-sm">{t('flatRate.sweep.approved')}</p>
        <p className="text-muted-foreground text-sm">{t('flatRate.sweep.told')}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('flatRate.sweep.cancel')}
          </Button>
          <Button type="button" disabled={busy} onClick={onConfirm}>
            {t(`flatRate.sweep.confirm.${direction}`)}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function RateField({
  label,
  value,
  ok,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly ok: boolean;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm font-semibold">
      {label}
      <Input
        inputMode="decimal"
        value={value}
        aria-invalid={value !== '' && !ok}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
